import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { useTheme } from './ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faTrash,
  faUsers,
  faEnvelope,
  faPhone,
  faCalendarAlt,
  faCheck,
  faTimes,
  faEye,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';

function AffiliateTeamManagement() {
  const { currentUser } = useContext(AuthContext);
  const { theme } = useTheme();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [affiliateDefaultRate, setAffiliateDefaultRate] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'member',
    campaignName: 'Team Member',
    commissionRate: ''
  });

  // Check if user is affiliate admin
  const isAffiliateAdmin = currentUser?.isAffiliateAdmin === 'True' || currentUser?.isAffiliateAdmin === true;

  useEffect(() => {
    if (isAffiliateAdmin && currentUser && currentUser.userId) {
      fetchTeamMembers();
      fetchAffiliateDefaultRate();
    }
  }, [currentUser, isAffiliateAdmin]);

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as (123) 456-7890
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumber.length <= 10) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
    } else {
      // Don't allow more than 10 digits
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({...formData, phone: formatted});
  };

  const validatePhone = (phone) => {
    // Check if phone matches (123) 456-7890 format
    const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    return phoneRegex.test(phone) || phone === '';
  };

  const fetchAffiliateDefaultRate = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://fuellinkapp.azurewebsites.net/api';
      const response = await fetch(`${apiUrl}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getAffiliateApplication',
          payload: {
            userId: currentUser.userId
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('getAffiliateApplication response:', result);
        
        // Handle direct response (result is the affiliate data itself, or null)
        if (result && result.DefaultCommissionRate) {
          setAffiliateDefaultRate(parseFloat(result.DefaultCommissionRate));
          console.log('Set affiliate default rate to:', result.DefaultCommissionRate);
        } else if (result === null) {
          console.log('No affiliate application found for user - using default 5%');
          setAffiliateDefaultRate(0.05); // Default to 5% if no application found
        }
        // Handle wrapped response format (fallback)
        else if (result && result.success && result.data && result.data.DefaultCommissionRate) {
          setAffiliateDefaultRate(parseFloat(result.data.DefaultCommissionRate));
          console.log('Set affiliate default rate to:', result.data.DefaultCommissionRate);
        }
        else {
          console.log('Unexpected response format - using default 5%');
          setAffiliateDefaultRate(0.05); // Default to 5% if unexpected response
        }
      } else {
        console.error('Failed to fetch affiliate application:', response.status);
      }
    } catch (error) {
      console.error('Error fetching affiliate default rate:', error);
    }
  };

  const isCommissionRateValid = (rateAsPercentage) => {
    const numRate = parseFloat(rateAsPercentage);
    const rateAsDecimal = numRate / 100;
    return !isNaN(numRate) && numRate >= 0 && rateAsDecimal <= affiliateDefaultRate;
  };

  const getCommissionRateError = (rateAsPercentage) => {
    const numRate = parseFloat(rateAsPercentage);
    if (isNaN(numRate) || numRate < 0) {
      return 'Commission rate must be a positive number';
    }
    const rateAsDecimal = numRate / 100;
    if (rateAsDecimal > affiliateDefaultRate) {
      return `Cannot exceed your affiliate rate of ${(affiliateDefaultRate * 100).toFixed(1)}%`;
    }
    return '';
  };

  const fetchTeamMembers = async () => {
    if (!currentUser || !currentUser.userId) {
      console.error('Current user or user ID not available');
      setTeamMembers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://fuellinkapp.azurewebsites.net/api';
      const response = await fetch(`${apiUrl}/affiliateManagement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getAffiliateTeamMembers',
          payload: {
            userId: currentUser.userId
          }
        })
      });

      const result = await response.json();
      
      if (result.success && result.data) {
        setTeamMembers(result.data);
      } else {
        console.error('Error in response:', result.error);
        setTeamMembers([]);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    
    if (!currentUser || !currentUser.userId) {
      alert('User session not available. Please refresh the page.');
      return;
    }
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://fuellinkapp.azurewebsites.net/api';
      const response = await fetch(`${apiUrl}/affiliateManagement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'inviteAffiliateMember',
          payload: {
            inviterUserId: currentUser.userId,
            memberFirstName: formData.firstName,
            memberLastName: formData.lastName,
            memberEmail: formData.email,
            phone: formData.phone,
            isAdmin: formData.role === 'admin',
            campaignName: formData.campaignName || 'Team Member',
            commissionRate: formData.commissionRate ? parseFloat(formData.commissionRate) / 100 : null
          }
        })
      });

      const result = await response.json();
      
      if (result.success) {
        // Close modal and refresh list
        setShowAddModal(false);
        resetForm();
        fetchTeamMembers();
        alert('Team member invitation sent successfully!');
      } else {
        alert(result.error || 'Failed to add team member');
      }
    } catch (error) {
      console.error('Error adding team member:', error);
      alert('Error adding team member');
    }
  };

  const handleEditMember = async (e) => {
    e.preventDefault();
    
    if (!currentUser || !currentUser.userId) {
      alert('User session not available. Please refresh the page.');
      return;
    }
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://fuellinkapp.azurewebsites.net/api';
      const response = await fetch(`${apiUrl}/affiliateManagement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'updateAffiliateTeamMember',
          payload: {
            userId: currentUser.userId,
            memberId: selectedMember.id,
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            role: formData.role,
            campaignName: formData.campaignName || 'Team Member',
            commissionRate: formData.commissionRate ? parseFloat(formData.commissionRate) / 100 : null
          }
        })
      });

      const result = await response.json();
      
      if (result.success) {
        // Close modal and refresh list
        setShowEditModal(false);
        setSelectedMember(null);
        resetForm();
        fetchTeamMembers();
        alert('Team member updated successfully!');
      } else {
        alert(result.error || 'Failed to update team member');
      }
    } catch (error) {
      console.error('Error editing team member:', error);
      alert('Error updating team member');
    }
  };

  const handleDeactivateMember = async (memberId) => {
    if (window.confirm('Are you sure you want to deactivate this team member? They will lose access to the affiliate portal.')) {
      
      if (!currentUser || !currentUser.uid) {
        alert('User session not available. Please refresh the page.');
        return;
      }
      
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://fuellinkapp.azurewebsites.net/api';
        const response = await fetch(`${apiUrl}/affiliateManagement`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'deactivateAffiliateTeamMember',
            payload: {
              userId: currentUser.userId,
              memberId: memberId
            }
          })
        });

        const result = await response.json();
        
        if (result.success) {
          fetchTeamMembers();
          alert('Team member deactivated successfully!');
        } else {
          alert(result.error || 'Failed to deactivate team member');
        }
      } catch (error) {
        console.error('Error deactivating member:', error);
        alert('Error deactivating team member');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: 'member',
      campaignName: 'Team Member',
      commissionRate: ''
    });
  };

  const openEditModal = (member) => {
    setSelectedMember(member);
    setFormData({
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      phone: member.phone,
      role: member.isAffiliateAdmin ? 'admin' : 'member',
      campaignName: member.campaignName || 'Team Member',
      commissionRate: member.commissionRate || ''
    });
    setShowEditModal(true);
  };

  // Redirect if not admin
  if (!isAffiliateAdmin) {
    return (
      <div className="p-6 text-center">
        <div className="max-w-md mx-auto">
          <FontAwesomeIcon icon={faUsers} className="text-6xl text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-600">
            Only affiliate administrators can manage team members. Contact your affiliate admin for access.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
        <div className="ml-4">
          <p>Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team Management</h1>
          <p className="text-gray-600">Manage your affiliate sales team members and their referral codes</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        >
          <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
          Add Team Member
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faUsers} className="text-blue-500 text-2xl mr-3" />
            <div>
              <div className="text-2xl font-bold text-gray-900">{teamMembers.length}</div>
              <div className="text-sm text-gray-600">Total Members</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faCheck} className="text-green-500 text-2xl mr-3" />
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {teamMembers.filter(m => m.status === 'active').length}
              </div>
              <div className="text-sm text-gray-600">Active Users</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faEye} className="text-orange-500 text-2xl mr-3" />
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {teamMembers.reduce((sum, m) => sum + (m.totalVisits || 0), 0)}
              </div>
              <div className="text-sm text-gray-600">Total Team Visits</div>
            </div>
          </div>
        </div>
      </div>

      {/* Team Members Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Referral Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {teamMembers.length > 0 ? teamMembers.map((member) => (
                <tr key={member.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {member.firstName || 'N/A'} {member.lastName || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        Joined {member.joinDate && member.joinDate !== 'N/A' ? new Date(member.joinDate).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <div className="flex items-center mb-1">
                        <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-gray-400" />
                        {member.email || 'N/A'}
                      </div>
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faPhone} className="mr-2 text-gray-400" />
                        {member.phone || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {member.referralCode || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>
                      <div>{member.totalVisits || 0} visits</div>
                      <div>{member.totalConversions || 0} conversions</div>
                      <div className="font-semibold text-green-600">${(member.totalEarnings || 0).toFixed(2)}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      member.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : member.status === 'invited'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {member.status === 'active' ? 'Active User' : 
                       member.status === 'invited' ? 'Invitation Sent' : 
                       'Pending Setup'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openEditModal(member)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={() => handleDeactivateMember(member.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    <div className="py-8">
                      <FontAwesomeIcon icon={faUsers} className="text-4xl mb-3 text-gray-300" />
                      <p>No team members yet</p>
                      <p className="text-sm">Add your first team member to get started</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: theme.cardBG || '#ffffff' }}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold" style={{ color: theme.text || '#111827' }}>
                  Invite Team Member
                </h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <form onSubmit={handleAddMember}>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label 
                        className="block text-sm font-medium mb-1"
                        style={{ color: theme.textSecondary || '#6b7280' }}
                      >
                        First Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label 
                        className="block text-sm font-medium mb-1"
                        style={{ color: theme.textSecondary || '#6b7280' }}
                      >
                        Last Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <div>
                    <label 
                      className="block text-sm font-medium mb-1"
                      style={{ color: theme.textSecondary || '#6b7280' }}
                    >
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="john.doe@example.com"
                    />
                  </div>

                  <div>
                    <label 
                      className="block text-sm font-medium mb-1"
                      style={{ color: theme.textSecondary || '#6b7280' }}
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label 
                      className="block text-sm font-medium mb-1"
                      style={{ color: theme.textSecondary || '#6b7280' }}
                    >
                      Campaign Name
                    </label>
                    <input
                      type="text"
                      value={formData.campaignName}
                      onChange={(e) => setFormData({...formData, campaignName: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Team Member"
                    />
                  </div>

                  <div>
                    <label 
                      className="block text-sm font-medium mb-1"
                      style={{ color: theme.textSecondary || '#6b7280' }}
                    >
                      Commission Rate (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.commissionRate}
                      onChange={(e) => setFormData({...formData, commissionRate: e.target.value})}
                      className={`w-full p-2 border rounded-md focus:ring-2 focus:border-transparent ${
                        formData.commissionRate && !isCommissionRateValid(formData.commissionRate)
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="5.0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter commission as a percentage (e.g., 5.0 for 5%). Cannot exceed your affiliate commission rate of {(affiliateDefaultRate * 100).toFixed(1)}%. If exceeded, payment will be limited to the maximum amount your affiliate account earns.
                    </p>
                    {formData.commissionRate && getCommissionRateError(formData.commissionRate) && (
                      <p className="text-xs text-red-500 mt-1">
                        {getCommissionRateError(formData.commissionRate)}
                      </p>
                    )}
                  </div>


                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isAdminAdd"
                      checked={formData.role === 'admin'}
                      onChange={(e) => setFormData({...formData, role: e.target.checked ? 'admin' : 'member'})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label 
                      htmlFor="isAdminAdd" 
                      className="ml-2 block text-sm"
                      style={{ color: theme.text || '#111827' }}
                    >
                      Make this member an affiliate admin
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 -mt-2">
                    Admins can invite team members and manage the affiliate account
                  </p>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Send Invitation
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {showEditModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: theme.cardBG || '#ffffff' }}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold" style={{ color: theme.text || '#111827' }}>
                  Edit Team Member
                </h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedMember(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <form onSubmit={handleEditMember}>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label 
                        className="block text-sm font-medium mb-1"
                        style={{ color: theme.textSecondary || '#6b7280' }}
                      >
                        First Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label 
                        className="block text-sm font-medium mb-1"
                        style={{ color: theme.textSecondary || '#6b7280' }}
                      >
                        Last Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label 
                      className="block text-sm font-medium mb-1"
                      style={{ color: theme.textSecondary || '#6b7280' }}
                    >
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label 
                      className="block text-sm font-medium mb-1"
                      style={{ color: theme.textSecondary || '#6b7280' }}
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label 
                      className="block text-sm font-medium mb-1"
                      style={{ color: theme.textSecondary || '#6b7280' }}
                    >
                      Campaign Name
                    </label>
                    <input
                      type="text"
                      value={formData.campaignName}
                      onChange={(e) => setFormData({...formData, campaignName: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label 
                      className="block text-sm font-medium mb-1"
                      style={{ color: theme.textSecondary || '#6b7280' }}
                    >
                      Commission Rate (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.commissionRate}
                      onChange={(e) => setFormData({...formData, commissionRate: e.target.value})}
                      className={`w-full p-2 border rounded-md focus:ring-2 focus:border-transparent ${
                        formData.commissionRate && !isCommissionRateValid(formData.commissionRate)
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="5.0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter commission as a percentage (e.g., 5.0 for 5%). Cannot exceed your affiliate commission rate of {(affiliateDefaultRate * 100).toFixed(1)}%. If exceeded, payment will be limited to the maximum amount your affiliate account earns.
                    </p>
                    {formData.commissionRate && getCommissionRateError(formData.commissionRate) && (
                      <p className="text-xs text-red-500 mt-1">
                        {getCommissionRateError(formData.commissionRate)}
                      </p>
                    )}
                  </div>


                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isAdminEdit"
                      checked={formData.role === 'admin'}
                      onChange={(e) => setFormData({...formData, role: e.target.checked ? 'admin' : 'member'})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label 
                      htmlFor="isAdminEdit" 
                      className="ml-2 block text-sm"
                      style={{ color: theme.text || '#111827' }}
                    >
                      Make this member an affiliate admin
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 -mt-2">
                    Admins can invite team members and manage the affiliate account
                  </p>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedMember(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AffiliateTeamManagement;